/**
 * DevLogTab - Timeline of development activity for a feature
 * 
 * Shows decisions, blockers, status updates, commits, PRs, issues, and comments
 * logged via MCP (Multi-Agent Collaboration Protocol) and GitHub webhooks.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Lightbulb, 
  AlertTriangle, 
  ArrowRight, 
  GitCommit, 
  CheckCircle,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Code,
  FileText,
  Clock,
  User,
  GitPullRequest,
  GitMerge,
  CircleDot,
  MessageSquare,
  ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useTimezone } from '../../hooks/useTimezone';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { featuresApi } from '../../services/api';

// Activity type configuration
const ACTIVITY_TYPES = {
  // MCP-sourced activities
  decision: {
    icon: Lightbulb,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-blue-300 dark:border-blue-700',
    label: 'Decision'
  },
  blocker: {
    icon: AlertTriangle,
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    borderColor: 'border-red-300 dark:border-red-700',
    label: 'Blocker'
  },
  blocker_cleared: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-300 dark:border-green-700',
    label: 'Blocker Cleared'
  },
  status_update: {
    icon: ArrowRight,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-700',
    label: 'Status Update'
  },
  commit_link: {
    icon: GitCommit,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-300 dark:border-green-700',
    label: 'Commit'
  },
  criteria_complete: {
    icon: CheckCircle,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    borderColor: 'border-emerald-300 dark:border-emerald-700',
    label: 'Requirement Done'
  },
  // GitHub-sourced activities
  pr_opened: {
    icon: GitPullRequest,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    borderColor: 'border-purple-300 dark:border-purple-700',
    label: 'PR Opened'
  },
  pr_merged: {
    icon: GitMerge,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    borderColor: 'border-purple-300 dark:border-purple-700',
    label: 'PR Merged'
  },
  pr_closed: {
    icon: GitPullRequest,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-700',
    label: 'PR Closed'
  },
  // Repo events (new schema)
  commit: {
    icon: GitCommit,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-300 dark:border-green-700',
    label: 'Commit'
  },
  // Ticket events (new schema)
  ticket_created: {
    icon: CircleDot,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-300 dark:border-green-700',
    label: 'Ticket Created'
  },
  ticket_closed: {
    icon: CheckCircle,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    borderColor: 'border-purple-300 dark:border-purple-700',
    label: 'Ticket Closed'
  },
  ticket_reopened: {
    icon: RefreshCw,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    borderColor: 'border-orange-300 dark:border-orange-700',
    label: 'Ticket Reopened'
  },
  ticket_comment: {
    icon: MessageSquare,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-blue-300 dark:border-blue-700',
    label: 'Comment'
  },
  // Legacy types (backwards compat)
  issue_created: {
    icon: CircleDot,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-300 dark:border-green-700',
    label: 'Ticket Created'
  },
  issue_closed: {
    icon: CheckCircle,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    borderColor: 'border-purple-300 dark:border-purple-700',
    label: 'Ticket Closed'
  },
  issue_reopened: {
    icon: RefreshCw,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    borderColor: 'border-orange-300 dark:border-orange-700',
    label: 'Ticket Reopened'
  },
  issue_comment: {
    icon: MessageSquare,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-blue-300 dark:border-blue-700',
    label: 'Comment'
  }
};

// Format timestamp for display (fallback - prefer using formatTimestamp prop)
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Single activity entry component
const ActivityEntry = ({ activity, colors, formatTimestamp: formatTimestampProp }) => {
  const [expanded, setExpanded] = useState(false);
  const typeConfig = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.decision;
  const Icon = typeConfig.icon;
  const hasCodeSnippet = activity.metadata?.code_snippet;
  const hasFilesChanged = activity.metadata?.files_changed?.length > 0;
  const isExpandable = hasCodeSnippet || hasFilesChanged;
  
  // Use the passed format function or the default
  const formatTime = formatTimestampProp || formatTimestamp;

  return (
    <div className="relative pl-8 pb-6 last:pb-0">
      {/* Timeline line */}
      <div className={cn(
        "absolute left-[11px] top-8 bottom-0 w-0.5",
        colors.bgTertiary
      )} />
      
      {/* Timeline dot */}
      <div className={cn(
        "absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center",
        typeConfig.bgColor
      )}>
        <Icon className={cn("w-3.5 h-3.5", typeConfig.color)} />
      </div>
      
      {/* Content */}
      <div className={cn(
        "rounded-lg border p-3",
        colors.bgCard,
        typeConfig.borderColor
      )}>
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={cn("text-xs", typeConfig.color)}>
              {typeConfig.label}
            </Badge>
            <span className={cn("text-xs flex items-center gap-1", colors.textMuted)}>
              <User className="w-3 h-3" />
              {activity.github_username && activity.is_external_user ? (
                <a 
                  href={`https://github.com/${activity.github_username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline text-blue-500"
                >
                  @{activity.github_username}
                </a>
              ) : (
                activity.user_name || activity.display_name || 'Unknown'
              )}
            </span>
            {(activity.source === 'github_webhook' || activity.channel === 'repo' || activity.channel === 'ticket') && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {activity.provider === 'jira' ? 'Jira' : activity.provider === 'linear' ? 'Linear' : activity.provider === 'gitlab' ? 'GitLab' : 'GitHub'}
              </Badge>
            )}
          </div>
          <span className={cn("text-xs flex items-center gap-1 whitespace-nowrap", colors.textMuted)}>
            <Clock className="w-3 h-3" />
            {formatTime(activity.created_at)}
          </span>
        </div>
        
        {/* Content text */}
        <p className={cn("text-sm mt-2", colors.textPrimary)}>
          {activity.content}
        </p>
        
        {/* External link for PR/Issue/Comment activities */}
        {activity.metadata && (activity.metadata.pr_url || activity.metadata.issue_url || activity.metadata.comment_url) && (
          <a
            href={activity.metadata.comment_url || activity.metadata.pr_url || activity.metadata.issue_url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("inline-flex items-center gap-1 text-xs mt-2 hover:underline", "text-blue-500")}
          >
            <ExternalLink className="w-3 h-3" />
            View
          </a>
        )}
        
        {/* Expandable section */}
        {isExpandable && (
          <div className="mt-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className={cn(
                "flex items-center gap-1 text-xs hover:underline",
                colors.textMuted
              )}
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {hasCodeSnippet && <><Code className="w-3 h-3" /> Code Snippet</>}
              {hasFilesChanged && <><FileText className="w-3 h-3" /> {activity.metadata.files_changed.length} files</>}
            </button>
            
            {expanded && (
              <div className="mt-2">
                {/* Code snippet */}
                {hasCodeSnippet && (
                  <pre className={cn(
                    "text-xs p-3 rounded overflow-x-auto font-mono",
                    "bg-gray-900 text-gray-100 dark:bg-gray-950"
                  )}>
                    {activity.metadata.code_snippet}
                  </pre>
                )}
                
                {/* Files changed */}
                {hasFilesChanged && (
                  <ul className={cn("text-xs space-y-1 mt-2", colors.textSecondary)}>
                    {activity.metadata.files_changed.map((file, i) => (
                      <li key={i} className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        <code className="font-mono">{file}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Status update metadata */}
        {activity.type === 'status_update' && activity.metadata?.old_status && (
          <div className={cn("flex items-center gap-2 mt-2 text-xs", colors.textMuted)}>
            <Badge variant="outline">{activity.metadata.old_status}</Badge>
            <ArrowRight className="w-3 h-3" />
            <Badge variant="outline">{activity.metadata.new_status}</Badge>
          </div>
        )}
      </div>
    </div>
  );
};

// Main DevLogTab component
const DevLogTab = ({ featureId }) => {
  const { getThemeColors } = useSafeMode();
  const { formatDateTime } = useTimezone();
  const colors = getThemeColors();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  
  // Use user's timezone for formatting timestamps
  const formatTimestampLocal = (timestamp) => {
    if (!timestamp) return '';
    return formatDateTime(timestamp);
  };
  
  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'decision', label: 'Decisions' },
    { value: 'commit_link', label: 'Commits' },
    { value: 'blocker', label: 'Blockers' },
    { value: 'status_update', label: 'Status' }
  ];
  
  const loadActivity = useCallback(async () => {
    if (!featureId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {};
      if (filter !== 'all') {
        params.activity_type = filter;
      }
      
      const response = await featuresApi.getActivity(featureId, params);
      setActivities(response.data?.activities || []);
    } catch (err) {
      console.error('Error loading dev log:', err);
      setError('Failed to load development activity');
    } finally {
      setLoading(false);
    }
  }, [featureId, filter]);
  
  useEffect(() => {
    loadActivity();
  }, [loadActivity]);
  
  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Filter className={cn("w-4 h-4", colors.textMuted)} />
          {filterOptions.map(opt => (
            <Button
              key={opt.value}
              variant={filter === opt.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilter(opt.value)}
              className="h-7 px-2 text-xs"
            >
              {opt.label}
            </Button>
          ))}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={loadActivity}
          disabled={loading}
          className="h-7"
        >
          <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
        </Button>
      </div>
      
      {/* Content */}
      {loading && activities.length === 0 ? (
        <div className={cn("text-center py-8", colors.textMuted)}>
          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Loading activity...
        </div>
      ) : error ? (
        <div className={cn("text-center py-8 text-red-500")}>
          {error}
        </div>
      ) : activities.length === 0 ? (
        <div className={cn("text-center py-8 space-y-2", colors.textMuted)}>
          <GitCommit className="w-8 h-8 mx-auto opacity-50" />
          <p className="text-sm">No development activity yet</p>
          <p className="text-xs max-w-xs mx-auto">
            Activity will appear here when developers log decisions or link commits via MCP.
          </p>
        </div>
      ) : (
        <div className="relative">
          {activities.map((activity, index) => (
            <ActivityEntry 
              key={activity.activity_id || index} 
              activity={activity}
              colors={colors}
              formatTimestamp={formatTimestampLocal}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DevLogTab;
